(function () {
  'use strict';

  // ECSR-821: focus() options are silently ignored on older engines, and the
  // UA then scrolls the focused element into view (page included). Restore
  // the scroll position if that happens — same guard the bundled Flickity
  // uses for its own focus call. Like that guard, this only restores window
  // scroll, synchronously: intermediate scrollable ancestors (e.g. the cart
  // drawer) and engines that scroll focus asynchronously are not covered.
  function focusWithoutScroll(el) {
    var x = window.pageXOffset;
    var y = window.pageYOffset;
    el.focus({ preventScroll: true });
    if (window.pageXOffset !== x || window.pageYOffset !== y) {
      window.scrollTo(x, y);
    }
  }

  function syncDotStates(dotsContainer) {
    var dots = dotsContainer.querySelectorAll('.dot');
    dots.forEach(function (dot, i) {
      var isSelected = dot.classList.contains('is-selected');
      dot.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      dot.setAttribute('tabindex', isSelected ? '0' : '-1');
      if (dot.getAttribute('aria-current') === 'step') {
        dot.setAttribute('aria-current', 'true');
      }
      if (!isSelected) {
        dot.removeAttribute('aria-current');
      }
    });
  }

  function enhanceFlickityDots(flickityInstance) {
    if (!flickityInstance || !flickityInstance.element) return;

    var element = flickityInstance.element;
    var dotsContainer = element.querySelector('.flickity-page-dots');
    if (!dotsContainer) return;
    if (dotsContainer.dataset.a11yEnhanced) return;
    dotsContainer.dataset.a11yEnhanced = 'true';

    dotsContainer.setAttribute('role', 'tablist');
    dotsContainer.setAttribute('aria-label', 'Slide navigation');

    var dots = dotsContainer.querySelectorAll('.dot');
    var totalSlides = dots.length;

    dots.forEach(function (dot, index) {
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', 'Go to slide ' + (index + 1) + ' of ' + totalSlides);
    });

    syncDotStates(dotsContainer);

    // Watch for Flickity re-setting aria-current="step" or class changes
    var observer = new MutationObserver(function (mutations) {
      var needsSync = false;
      for (var m = 0; m < mutations.length; m++) {
        var mutation = mutations[m];
        if (mutation.attributeName === 'aria-current' && mutation.target.getAttribute('aria-current') === 'step') {
          needsSync = true;
          break;
        }
        if (mutation.attributeName === 'class') {
          needsSync = true;
          break;
        }
      }
      if (needsSync) {
        syncDotStates(dotsContainer);
      }
    });

    dots.forEach(function (dot) {
      observer.observe(dot, { attributes: true, attributeFilter: ['aria-current', 'class'] });
    });

    var liveRegion = element.querySelector('[data-flickity-live-region]');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.setAttribute('data-flickity-live-region', '');
      element.appendChild(liveRegion);
    }

    dotsContainer.addEventListener('keydown', function (e) {
      var currentDots = dotsContainer.querySelectorAll('.dot');
      var currentIndex = Array.from(currentDots).indexOf(document.activeElement);
      if (currentIndex === -1) return;

      var newIndex = currentIndex;

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          newIndex = (currentIndex + 1) % currentDots.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          newIndex = (currentIndex - 1 + currentDots.length) % currentDots.length;
          break;
        case 'Home':
          e.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          newIndex = currentDots.length - 1;
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          flickityInstance.select(currentIndex);
          return;
        default:
          return;
      }

      currentDots[currentIndex].setAttribute('tabindex', '-1');
      currentDots[newIndex].setAttribute('tabindex', '0');
      // ECSR-821: keep focus moves from scrolling the page; the change
      // handler below reveals the dot within its own row.
      focusWithoutScroll(currentDots[newIndex]);
      flickityInstance.select(newIndex);
    });

    flickityInstance.on('change', function (index) {
      var currentDots = dotsContainer.querySelectorAll('.dot');
      var activeDot = currentDots[index];
      liveRegion.textContent = 'Slide ' + (index + 1) + ' of ' + currentDots.length;

      if (activeDot && dotsContainer.contains(document.activeElement)) {
        focusWithoutScroll(activeDot);
      }

      // ECSR-514: on mobile, the dots row can be narrower than its content
      // and scrolls internally (see assets/custom-product.css). Advancing
      // slides via swipe or the prev/next arrows only fires this `change`
      // event with no focus inside the dots row, so the `.focus()` call
      // above never runs and the active dot can end up scrolled out of
      // view with no visible indication of the current slide.
      // ECSR-821: scrollIntoView({block:'nearest'}) still scrolls ancestor
      // containers (including the page) when the dot is vertically offscreen —
      // on tall carousels like the PDP Compare Table the dots sit below the
      // fold, so every slide change yanked the page down to the bottom of the
      // table. Scroll only the dots row's own horizontal overflow instead.
      // Only rows that can actually scroll get the reveal — writing
      // scrollLeft on an overflow:visible/hidden row is at best a no-op and
      // at worst shifts content with no user-reachable way to scroll back.
      // (Most dots rows in the theme are not scrollable; only the mobile PDP
      // media-controls row is.) The geometry check then skips the layout
      // reads when a scrollable row happens not to overflow.
      var overflowX = getComputedStyle(dotsContainer).overflowX;
      if (overflowX !== 'auto' && overflowX !== 'scroll') return;
      if (dotsContainer.scrollWidth <= dotsContainer.clientWidth) return;

      if (activeDot) {
        var dotRect = activeDot.getBoundingClientRect();
        var containerRect = dotsContainer.getBoundingClientRect();
        if (dotRect.left < containerRect.left) {
          dotsContainer.scrollLeft += dotRect.left - containerRect.left;
        } else if (dotRect.right > containerRect.right) {
          dotsContainer.scrollLeft += dotRect.right - containerRect.right;
        }
      }
    });
  }

  // ECSR-1261 (AMP31): on product carousels, activating the Previous/Next
  // arrow with the keyboard (Enter or Space) leaves focus on the arrow while
  // new product cards slide in. Once the slide settles, move focus to the
  // first focusable element of the first product card newly brought into
  // view, so keyboard and screen reader shoppers land on the new products.
  // Pointer clicks (event.detail >= 1) keep today's behaviour untouched.
  var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var NAV_BUTTON_SELECTOR = '.flickity-prev-next-button';
  var PRODUCT_CARD_SELECTOR = '.product-card';
  var SETTLE_FALLBACK_MS = 1500;
  var lastKeyActivatedButton = null;
  var pendingFocus = typeof WeakMap === 'function' ? new WeakMap() : null;

  function getFlickityInstance(element) {
    var Flickity = window.FoxTheme && window.FoxTheme.Flickity;
    var instance = Flickity && typeof Flickity.data === 'function' ? Flickity.data(element) : null;
    if (!instance && element.slider && element.slider.instance) instance = element.slider.instance;
    return instance || null;
  }

  // Cells at least half inside the carousel viewport, in reading order.
  function getVisibleCells(instance) {
    var viewport = instance.viewport || instance.element;
    var viewportRect = viewport.getBoundingClientRect();
    var visible = [];
    (instance.cells || []).forEach(function (cell) {
      var rect = cell.element.getBoundingClientRect();
      if (!rect.width) return;
      var overlap = Math.min(rect.right, viewportRect.right) - Math.max(rect.left, viewportRect.left);
      if (overlap >= rect.width / 2) visible.push({ element: cell.element, left: rect.left });
    });
    var rtl = instance.options && instance.options.rightToLeft;
    visible.sort(function (a, b) { return rtl ? b.left - a.left : a.left - b.left; });
    return visible.map(function (item) { return item.element; });
  }

  function getFirstFocusable(cellElement) {
    var candidates = cellElement.querySelectorAll(FOCUSABLE_SELECTOR);
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].getClientRects().length) return candidates[i];
    }
    return null;
  }

  function focusNewlyShownCard(instance, state) {
    var after = getVisibleCells(instance);
    var newlyShown = after.filter(function (el) { return state.before.indexOf(el) === -1; });
    var candidates = newlyShown.length ? newlyShown : after;
    var target = null;
    var targetCell = null;
    for (var i = 0; i < candidates.length && !target; i++) {
      if (!candidates[i].querySelector(PRODUCT_CARD_SELECTOR)) continue;
      target = getFirstFocusable(candidates[i]);
      targetCell = candidates[i];
    }
    if (!target) return;

    // Flickity marks every cell outside the selected slide aria-hidden, even
    // when it is on screen (e.g. one-card steps). A focused card must be
    // exposed to assistive technology, so lift it from the card being
    // focused and hand control back to Flickity at the next slide change.
    if (targetCell.getAttribute('aria-hidden') === 'true') {
      targetCell.removeAttribute('aria-hidden');
      instance.once('select', function () {
        var selectedCells = (instance.selectedSlide && instance.selectedSlide.cells) || [];
        var stillSelected = selectedCells.some(function (cell) { return cell.element === targetCell; });
        if (!stillSelected) targetCell.setAttribute('aria-hidden', 'true');
      });
    }

    focusWithoutScroll(target);
  }

  function isKeyboardActivation(event, button) {
    if (event.detail === 0) return true;
    return lastKeyActivatedButton === button;
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    var button = event.target && event.target.closest ? event.target.closest(NAV_BUTTON_SELECTOR) : null;
    lastKeyActivatedButton = button;
  }, true);

  document.addEventListener('pointerdown', function () {
    lastKeyActivatedButton = null;
  }, true);

  // Capture phase: runs before Flickity's own click handler on the button,
  // so the cards visible before the move can be recorded.
  document.addEventListener('click', function (event) {
    var button = event.target && event.target.closest ? event.target.closest(NAV_BUTTON_SELECTOR) : null;
    if (!button || !pendingFocus) return;
    var keyboard = isKeyboardActivation(event, button);
    lastKeyActivatedButton = null;
    if (!keyboard || button.disabled) return;

    var carousel = button.parentElement;
    if (!carousel || !carousel.querySelector(PRODUCT_CARD_SELECTOR)) return;
    var instance = getFlickityInstance(carousel);
    if (!instance || !instance.cells || !instance.cells.length) return;

    var state = pendingFocus.get(carousel);
    if (state) {
      // A move is already in flight: keep the cards visible before it.
      clearTimeout(state.timer);
      instance.off('settle', state.onSettle);
    } else {
      state = { before: getVisibleCells(instance) };
      pendingFocus.set(carousel, state);
    }

    var startIndex = instance.selectedIndex;
    var done = false;
    state.onSettle = function () {
      if (done) return;
      done = true;
      clearTimeout(state.timer);
      instance.off('settle', state.onSettle);
      pendingFocus.delete(carousel);
      if (instance.selectedIndex === startIndex && !state.moved) return;
      focusNewlyShownCard(instance, state);
    };
    instance.once('select', function () { state.moved = true; });
    instance.on('settle', state.onSettle);
    // Safety net if the slide never reports settling (e.g. instant moves).
    state.timer = setTimeout(state.onSettle, SETTLE_FALLBACK_MS);
  }, true);

  window.FoxTheme = window.FoxTheme || {};
  window.FoxTheme.enhanceFlickityDots = enhanceFlickityDots;
})();
